import * as React from 'react';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import { Avatar, Button, Chip, Tooltip, Typography } from '@mui/material';
import { colors } from '../../assets/utils/constants';
import { Box } from '@mui/system';
import { PinDropOutlined, Preview } from '@mui/icons-material';
import { useState } from 'react';
import CustomDialog from '../Dialogs/CustomDialog';
import ORGDetails from '../../pages/practicalTraining/components/ORGDetails';

// ######################## Table column #######################
const columns = [
    { id: 's_n', label: 'S/N', minWidth: 1, align: 'center' },
    { id: 'name', label: 'Applicant name' },
    {
        id: 'status',
        label: 'Status',
        align: 'center',
    },
    {
        id: 'action',
        label: 'Action',
        align: 'center',
    },
];
function createData(s_n, name, status, action) {
    return {
        s_n,
        name,
        status,
        action,
    };
}

// ######################## USER CARD #######################
const UserCard = ({ user }) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            borderRadius: 3,
            position: 'relative',
            overflow: 'hidden',
        }}
    >
        <Avatar
            src={user.org_logo}
            sx={{
                width: 65,
                height: 65,
                mr: 2,
                p: 0.5,
                border: `1px dotted ${colors.info}`,
                background: `linear-gradient(180deg, ${colors.bgColor1}, ${colors.bgColor3})`,
                img: {
                    objectFit: 'contain',
                },
            }}
        />
        <Box>
            <Typography
                sx={{
                    fontSize: 16,
                    color: colors.info,
                    fontWeight: 'bold',
                }}
                noWrap
            >
                {user.organization_name}
            </Typography>
            <Typography
                sx={{
                    fontSize: 13,
                    display: 'flex',
                    alignItems: 'center',
                    color: colors.primary,
                    mt: 1,
                    fontWeight: 'bold',
                    opacity: 0.8,
                }}
            >
                <PinDropOutlined color="warning" sx={{ mr: 1, fontSize: 15 }} />
                {user.location}
            </Typography>
        </Box>
    </Box>
);

// !###################### MAIN FUNC ########################
const PTInstitutionApprovalTable = ({ institutions, height }) => {
    // -> Table state
    const [page, setPage] = useState(0);
    const [institutionDetails, setInstitutionDetails] = useState({});
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(+event.target.value);
        setPage(0);
    };

    // -> Dialogy state
    const [openDialogy, setopenDialogy] = useState(false);
    const handleOpenDialogy = () => {
        setopenDialogy(true);
    };
    const handleCloseDialogy = () => {
        setopenDialogy(false);
    };

    // ######################## TABLE ROW #######################
    const rows = institutions?.map((institution, index) => {
        return createData(
            <Typography sx={{ fontSize: 18, fontWeight: 'bold', opacity: 0.8 }}>
                {++index}
            </Typography>,
            <UserCard user={institution} />,
            <Chip
                size="small"
                color={
                    institution.status === 'pending'
                        ? 'warning'
                        : institution.status === 'approved'
                        ? 'success'
                        : institution.status === 'declined'
                        ? 'error'
                        : 'default'
                }
                label={institution.status}
            />,
            //? #################### ACTION BUTTON #######################
            <Box
                sx={{
                    // display: 'flex',
                    flexDirection: 'column',
                    alignContent: 'end',
                    textAlign: 'center',
                }}
            >
                <Tooltip title="View applicant profile" arrow>
                    <Button
                        sx={{
                            fontSize: 12,
                            fontWeight: 'bold',
                            pt: 0.1,
                            pb: 0.1,
                            m: 0.4,
                        }}
                        color="info"
                        variant="outlined"
                        onClick={() => {
                            setInstitutionDetails(institution);
                            handleOpenDialogy();
                        }}
                    >
                        <Preview sx={{ fontSize: 12, mr: 0.4 }} />
                        More details
                    </Button>
                </Tooltip>
            </Box>,
        );
    });

    return (
        <React.Fragment>
            {/* ############## DIALOGY ############# */}
            <CustomDialog
                head={'Institution details'}
                openDialog={openDialogy}
                customWidth={'md'}
                handleDialogClose={handleCloseDialogy}
            >
                <ORGDetails organization={institutionDetails} />
            </CustomDialog>

            <Paper
                sx={{ width: '100%', overflow: 'hidden', boxShadow: 'none' }}
            >
                <TableContainer
                    sx={{
                        height: height ? height : '61.9vh',
                        background: `linear-gradient(180deg, ${colors.bgColor1}, ${colors.bgColor2})`,
                    }}
                >
                    <Table stickyHeader aria-label="sticky table">
                        <TableHead>
                            <TableRow>
                                {columns.map((column) => (
                                    <TableCell
                                        key={column.id}
                                        align={column.align}
                                        sx={{
                                            textTransform: 'uppercase',
                                            fontWeight: 'bold',
                                            background: `linear-gradient(180deg, ${colors.bgColor1}, ${colors.bgColor3})`,
                                            fontSize: 12,
                                            color: colors.primary,
                                            whiteSpace: 'nowrap',
                                            opacity: 0.8,
                                        }}
                                        style={{ minWidth: column.minWidth }}
                                    >
                                        {column.label}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows
                                .slice(
                                    page * rowsPerPage,
                                    page * rowsPerPage + rowsPerPage,
                                )
                                .map((row, index) => {
                                    return (
                                        <TableRow
                                            hover
                                            role="checkbox"
                                            tabIndex={-1}
                                            key={index}
                                            sx={{
                                                background: `linear-gradient(180deg, ${colors.bgColor1}, ${colors.bgColor2})`,
                                            }}
                                        >
                                            {columns.map((column) => {
                                                const value = row[column.id];
                                                return (
                                                    <TableCell
                                                        key={column.id}
                                                        align={column.align}
                                                    >
                                                        {column.format &&
                                                        typeof value ===
                                                            'number'
                                                            ? column.format(
                                                                  value,
                                                              )
                                                            : value}
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    );
                                })}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[1, 2, 5, 10]}
                    component="div"
                    count={rows.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    sx={{
                        background: `linear-gradient(180deg, ${colors.bgColor1}, ${colors.bgColor3})`,
                    }}
                />
            </Paper>
        </React.Fragment>
    );
};

export default PTInstitutionApprovalTable;
